/**
 * Hook for fetching existing portfolio snapshots in followup comparison views.
 * Mirrors usePortfolioSnapshot but fetches from existing_* snapshot types.
 * Only enabled when isFollowup is true — returns null immediately otherwise.
 */

import type { GoalSnapshotResponse } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import * as advisorApi from "@/lib/api/flow-instances/portfolio-snapshots";
import * as publicApi from "@/lib/api/public-flow/portfolio-snapshots";
import { usePublicSession } from "@/lib/context/public-session-context";

type ExistingSnapshotType = "xray" | "assetAllocation";

type SnapshotHookResult = {
  data: unknown | null;
  generatedAt: string | null;
  isLoading: boolean;
};

const ADVISOR_FN: Record<
  ExistingSnapshotType,
  (portfolioId: string) => Promise<GoalSnapshotResponse>
> = {
  xray: advisorApi.getExistingXraySnapshot,
  assetAllocation: advisorApi.getExistingAssetAllocationSnapshot,
};

const PUBLIC_FN: Record<
  ExistingSnapshotType,
  (token: string, portfolioId: string) => Promise<GoalSnapshotResponse>
> = {
  xray: publicApi.getPublicExistingXraySnapshot,
  assetAllocation: publicApi.getPublicExistingAssetAllocationSnapshot,
};

export function useExistingPortfolioSnapshot(
  snapshotType: ExistingSnapshotType,
  portfolioId: string | null,
  isFollowup: boolean,
): SnapshotHookResult {
  const session = usePublicSession();
  const isPublic = session.isPublic;
  const prefix = isPublic ? "publicFlow" : "flowInstances";
  const queryKey = [prefix, "portfolios", portfolioId, "existing-snapshot", snapshotType] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => {
      if (session.isPublic) {
        return PUBLIC_FN[snapshotType](session.sessionToken, portfolioId!);
      }
      return ADVISOR_FN[snapshotType](portfolioId!);
    },
    enabled: isFollowup && !!portfolioId,
  });

  return {
    data: data?.data ?? null,
    generatedAt: data?.generatedAt ?? null,
    isLoading,
  };
}
